import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, ProgressBar, OverlayTrigger, Tooltip, FormCheck, Alert } from 'react-bootstrap';
import { readContract, writeContract, getWalletClient, waitForTransactionReceipt } from '@wagmi/core';
import communitiesContractABI from '../../contracts/tgetherCommunities.json';
import LinkTokenABI from '../../contracts/LinkToken.json';
import { formatEther } from 'viem'; // For ETH <-> Wei conversion
import { useNavigate } from 'react-router-dom';
import * as config from '../../config.tsx';
const communitiesContractAddress = process.env.REACT_APP_COMMUNITIES_CONTRACT_ADDRESS as `0x${string}`;
const linkTokenContract = process.env.REACT_APP_CHAINLINK_TOKEN_CONTRACT as `0x${string}`;

interface CommunityProposalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  communityName: string;
}

function CommunityProposal({ show, setShow, communityName }: CommunityProposalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [showWaitAlert, setShowWaitAlert] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [feeChecked, setFeeChecked] = useState(false);
  const [feeInEth, setFeeInEth] = useState<string>(''); // Fee displayed in ETH
  const totalSteps = showAdvanced ? 9 : 8;

  const [createFormData, setCreateFormData] = useState({
    minCredsToProposeVote: '0',
    minCredsToVote: '0',
    maxCredsCountedForVote: '0',
    minProposalVotes: '0',
    memberAccessContract: '0x0000000000000000000000000000000000000000',
    proposalTime: '0',
    proposalDelay: '0',
    isInviteOnly: false,
    feeInWei: '0', // Fee stored in Wei for the transaction
  });

  
  const navigate = useNavigate();

  const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCreateFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prevStep => prevStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prevStep => prevStep - 1);
    }
  };

  const handleAdvancedChange = () => {
    setShowAdvanced(!showAdvanced);
  };

  const approveLinkFee= async () => {

    const tx = await writeContract(config.config, {
      address: linkTokenContract,   // LINK token contract address
      abi: LinkTokenABI,            // Correct ABI for the LINK token
      functionName: 'approve',      // The function name (approve)
      args: [ process.env.REACT_APP_FUND_CONTRACT, BigInt("200000000000000000")],
      value: BigInt("0"),           // No value for this transaction
    });

    // Wait for the transaction to be mined
    const receipt = await waitForTransactionReceipt(config.config,{
      hash: tx,  // Transaction hash from the tx response
    });
    setFeeInEth(formatEther(BigInt("0")));
    setFeeChecked(true)
  }

  // Fetch the fee from the contract using readContract
  const calculateFee = async () => {
    if (process.env.REACT_APP_CHAIN_ID == "0x66eee"){
      setFeeInEth(formatEther(BigInt("0")));
      setFeeChecked(true)
    } else{
    try {
      const feeData = await readContract(config.config, {
        abi: communitiesContractABI.abi,
        address: communitiesContractAddress,
        functionName: 'fee',
      });

      const feeInWei = BigInt(Number(feeData).toString());
      const feeInEth = formatEther(feeInWei); // Convert Wei to ETH for display

      setFeeInEth(feeInEth);
      setCreateFormData(prevState => ({
        ...prevState,
        feeInWei: feeInWei.toString(), // Store in Wei for the transaction
      }));
      setFeeChecked(true);
    } catch (error) {
      console.error('Error fetching fee from contract:', error);
    }

  }
  };

  // Handle the creation of the community proposal
  const handleCreateCommunityProposal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!communityName || communityName.trim() === '') {
      console.error('Community name is required and must be a string.');
      return;
    }

    try {
      // Get the signer for sending the transaction
      const walletClient = await getWalletClient(config.config);
      if (!walletClient) {
        console.error('No wallet client available.');
        return;
      }

      const tx = await writeContract(config.config, {
        address: communitiesContractAddress,
        abi: communitiesContractABI.abi,
        functionName: 'CommunityProposal',
        args: [
          communityName,
          Number(createFormData.minCredsToProposeVote),
          Number(createFormData.minCredsToVote),
          Number(createFormData.maxCredsCountedForVote),
          Number(createFormData.minProposalVotes),
          createFormData.memberAccessContract,
          Number(createFormData.proposalTime),
          Number(createFormData.proposalDelay),
          createFormData.isInviteOnly,
        ],
        value: BigInt(createFormData.feeInWei), // Send the fee in Wei
      });
      setShowWaitAlert(true);

      const receipt = await waitForTransactionReceipt(config.config,{
        hash: tx, // Access the transaction hash
      });
      const hexValue = receipt.logs[0].topics[1]; // example: "0x0000000000000000000000000000000000000000000000000000000000000006"
      const numberValue = Number(hexValue); // Converts hex to integer
      navigate(`/search-results?type=Proposal&value=${encodeURIComponent(numberValue)}`);


    } catch (error) {
      console.error('Error creating community proposal:', error);
    }
  };

  const handleInviteOnlyChange = (isInviteOnly) => {
    setCreateFormData(prevState => ({
      ...prevState,
      isInviteOnly
    }));
  };

  const feeCheckTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Calculate fee before proceeding.
    </Tooltip>
  );

  const renderTooltip = (text: string) => (
    <Tooltip id="button-tooltip">{text}</Tooltip>
  );
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Form.Group>
            <Form.Label>
              Min Creds to Propose Vote
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("This value sets the number of creds a member needs to propose a vote.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="minCredsToProposeVote"
              value={createFormData.minCredsToProposeVote}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 2:
        return (
          <Form.Group>
            <Form.Label>
              Min Creds to Vote
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("This value sets the minimum number of creds needed to vote on a proposal.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="minCredsToVote"
              value={createFormData.minCredsToVote}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 3:
        return (
          <Form.Group>
            <Form.Label>
              Max Creds Counted For Vote
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("This value sets the maximum amount of creds counted per member vote.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="maxCredsCountedForVote"
              value={createFormData.maxCredsCountedForVote}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 4:
        return (
          <Form.Group>
            <Form.Label>
              Min Proposal Votes
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("This value sets the minimum number of votes needed for a proposal to pass.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="minProposalVotes"
              value={createFormData.minProposalVotes}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 5:
        return (
          <Form.Group>
            <Form.Label>
              Proposal Time (in seconds)
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("This value sets the time a proposal will remain active.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="proposalTime"
              value={createFormData.proposalTime}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 6:
        return (
          <Form.Group>
            <Form.Label>
              Proposal Delay (in seconds)
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("This value sets a delay before the proposal vote time begins.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="proposalDelay"
              value={createFormData.proposalDelay}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 7:
        return (
          <Form.Group>
            <Form.Label>
              Is Invite Only
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("This value sets whether the community will be invite-only.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <div className="mb-2">
              <Button
                variant={createFormData.isInviteOnly ? "info" : "outline-info"}
                className="mr-2"
                onClick={() => handleInviteOnlyChange(true)}
              >
                True
              </Button>
              <Button
                variant={!createFormData.isInviteOnly ? "info" : "outline-info"}
                onClick={() => handleInviteOnlyChange(false)}
              >
                False
              </Button>
            </div>
          </Form.Group>
        );
      case 8:
        if (process.env.REACT_APP_CHAIN_ID == "0x66eee"){
          return(
            <div>
            <p>Press the button to approve the testnet LINK transfer. You can go to 
              <a href="https://faucets.chain.link/arbitrum-sepolia" target="_blank" rel="noopener noreferrer">
                Arbitrum Sepolia Faucet
              </a> to get funds.</p>
            <Button style={{marginBottom:"10px"}}variant="outline-info" onClick={approveLinkFee}>
              Approve 0.2 LINK for Fee
            </Button>
          </div>


          )

        }else{
        return (
          <Form.Group>
            <Form.Label>
              Fee
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip(
                  "This fee covers the gas cost for proposal execution. By signing this transaction, you acknowledge that this fee goes into a shared pool to fund automated upkeep and not directly tied to individual execution."
                )}
              >
                <Button
                  variant="secondary"
                  className="ml-1 p-0"
                  style={{ border: "none", background: "none" }}
                >
                  <i className="bi bi-patch-question-fill"></i>
                </Button>


              </OverlayTrigger>
            </Form.Label>
            <Button variant="outline-info" className="ml-1" onClick={calculateFee}>
              Calculate Fee
            </Button>
            <Form.Control
              type="number"
              name="fee"
              value={feeInEth}
              readOnly
            />
          </Form.Group>
        )};
        case 9:
          return (
            <Form.Group>
              <Form.Label>
                Member Access Contract
                <OverlayTrigger
                  placement="right"
                  overlay={renderTooltip("This is an advanced setting that allows the community to set an external contract to handle member invitations and credentials.")}
                >
                  <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                    <i className="bi bi-patch-question-fill"></i>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                type="text"
                name="memberAccessContract"
                value={createFormData.memberAccessContract}
                onChange={handleCreateInputChange}
              />
            </Form.Group>
          );
        default:
          return null;
      }
    };
  
    return (
      <>
        {showWaitAlert && (
          <Alert variant="warning" onClose={() => setShowWaitAlert(false)} dismissible>
            Proposal Creating...
          </Alert>
        )}
        <Modal show={show} onHide={() => setShow(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Create a Community Proposal</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="advanced-parameters-switch">
              <FormCheck type="switch" id="custom-switch" label="Advanced Parameters" onClick={handleAdvancedChange} />
            </div>
            <Form onSubmit={handleCreateCommunityProposal}>
              {renderStep()}
              <ProgressBar now={(currentStep / totalSteps) * 100} />
              <div className="mt-3 d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={handlePrev} disabled={currentStep === 1}>
                  Previous
                </Button>
                {currentStep < totalSteps ? (
                  <Button variant="info" type="button" onClick={handleNext}>
                    Next
                  </Button>
                ) : (
                  <OverlayTrigger placement="top" overlay={feeCheckTooltip}>
                    <span className="d-inline-block">
                      <Button variant="info" type="submit" disabled={!feeChecked}>
                        Create Proposal
                      </Button>
                    </span>
                  </OverlayTrigger>
                )}
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </>
    );
  }
  
  export default CommunityProposal;